import { useEffect, useRef, useState } from 'react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '../../../components/ui/carousel';
import Autoplay from 'embla-carousel-autoplay';

interface NewsItem {
  source?:      { id?: number; name?: string };
  author?:      string;
  title?:       string;
  description?: string;
  url?:         string;
  urlToImage?:  string;
  publishedAt?: string;
  content?:     string;
}

interface NewsFeedProps {
  newsItems:    NewsItem[];
  title?:       string;
  description?: string;
}

function NewsImagePlaceholder({ source }: { source?: string }) {
  return (
    <div
      className="w-full h-full flex flex-col items-center justify-center gap-3 group-hover:scale-105 transition-transform duration-700"
      style={{
        background: 'linear-gradient(135deg, #052e16 0%, #0a3d1f 60%, #14532d 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Subtle dot grid */}
      <svg
        aria-hidden="true"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.07 }}
      >
        <defs>
          <pattern id="dots" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1.2" fill="#4ade80" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#dots)" />
      </svg>

      {/* Leaf icon */}
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="rgba(74,222,128,0.55)"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ width: 40, height: 40, position: 'relative' }}
      >
        <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z" />
        <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" />
      </svg>

      {source && (
        <span style={{
          fontFamily: 'var(--font-body)',
          fontSize: 9,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: 'rgba(74,222,128,0.45)',
          position: 'relative',
        }}>
          {source}
        </span>
      )}
    </div>
  );
}

function formatDate(str?: string) {
  if (!str) return '';
  return new Date(str).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

export default function NewsFeed({
  newsItems,
  title       = 'In The Field',
  description = 'The latest developments in wildlife conservation, legal victories, and community action from across Africa.',
}: NewsFeedProps) {
  const ref     = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="w-full py-20 lg:py-28 bg-[#fafaf7] overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">

        {/* Header */}
        <div className={`flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6 reveal ${visible ? 'visible' : ''}`}>
          <div>
            <span className="flex items-center gap-3 mb-4">
              <span className="h-px w-8 bg-green-600" />
              <span
                className="text-green-700 text-xs tracking-[0.2em] uppercase font-medium"
                style={{ fontFamily: 'var(--font-body)' }}
              >
                Latest News
              </span>
            </span>
            <h2
              className="text-4xl font-bold text-gray-900"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              {title}
            </h2>
          </div>
          <p
            className="text-gray-500 max-w-sm text-sm leading-relaxed"
            style={{ fontFamily: 'var(--font-body)' }}
          >
            {description}
          </p>
        </div>

        {/* Cards */}
        {newsItems && newsItems.length > 0 ? (
          <div className={`reveal reveal-delay-2 ${visible ? 'visible' : ''} relative`}>
            <Carousel
              opts={{ loop: true, align: 'start' }}
              plugins={[Autoplay({ delay: 4500 })]}
            >
              <CarouselContent className="-ml-5">
                {newsItems.map((item, i) => (
                  <CarouselItem
                    key={i}
                    className="pl-5 md:basis-1/2 lg:basis-1/3"
                  >
                    <a
                      href={item.url ?? '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex flex-col rounded-2xl overflow-hidden border border-gray-100 bg-white hover:shadow-xl hover:shadow-green-100/60 transition-all duration-500 hover:-translate-y-1 h-full"
                    >
                      <div className="aspect-video overflow-hidden shrink-0">
                        {item.urlToImage ? (
                          <img
                            src={item.urlToImage}
                            alt={item.title ?? ''}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                          />
                        ) : (
                          <NewsImagePlaceholder source={item.source?.name} />
                        )}
                      </div>
                      <div className="flex flex-col flex-1 p-6">
                        {item.publishedAt && (
                          <p
                            className="text-xs text-green-600 font-semibold mb-2 tracking-wide"
                            style={{ fontFamily: 'var(--font-body)' }}
                          >
                            {formatDate(item.publishedAt)}
                          </p>
                        )}
                        {item.source?.name && (
                          <p
                            className="text-[10px] tracking-widest uppercase text-gray-400 mb-2"
                            style={{ fontFamily: 'var(--font-body)' }}
                          >
                            {item.source.name}
                          </p>
                        )}
                        <h3
                          className="font-semibold text-gray-900 mb-3 leading-snug line-clamp-2 group-hover:text-green-800 transition-colors"
                          style={{ fontFamily: 'var(--font-display)' }}
                        >
                          {item.title}
                        </h3>
                        <p
                          className="text-sm text-gray-500 leading-relaxed line-clamp-3 flex-1"
                          style={{ fontFamily: 'var(--font-body)' }}
                        >
                          {item.description}
                        </p>
                        <span
                          className="mt-5 inline-flex items-center gap-1 text-green-700 text-xs font-semibold group-hover:gap-2 transition-all"
                          style={{ fontFamily: 'var(--font-body)' }}
                        >
                          Read more →
                        </span>
                      </div>
                    </a>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="hidden md:flex -left-5 bg-white border-gray-200 hover:bg-green-50 hover:border-green-300" />
              <CarouselNext    className="hidden md:flex -right-5 bg-white border-gray-200 hover:bg-green-50 hover:border-green-300" />
            </Carousel>
          </div>
        ) : (
          <div
            className="text-center py-20 text-gray-300 text-sm"
            style={{ fontFamily: 'var(--font-body)' }}
          >
            No news items available at this time.
          </div>
        )}
      </div>
    </section>
  );
}
